/**
 * Home Maintenance plugin manifest — proof-of-concept.
 * Includes reducer, saga, and lazy route references for dynamic loading.
 */

import React from 'react';
import { PluginRegistration } from '@/infra/plugin-registry';
import { ServiceCallRenderer, MaintenanceTaskRenderer } from './components/TodoRenderer';
import { maintenanceReducer } from './ducks';
import { maintenanceSaga } from './sagas';
// Side-effect: register the equipment @mention provider with the mentions registry.
import './mention-provider';

// Lazy-loaded route components — only loaded when the user navigates to them.
const LazyMaintenancePage = React.lazy(
  () => import('./pages/MaintenancePage')
);
const LazyEquipmentDetailPage = React.lazy(
  () => import('./pages/EquipmentDetailPage')
);

export const maintenanceManifest: PluginRegistration = {
  id: 'maintenance',
  display_name: 'Home Maintenance',
  description: 'Track appliances, service schedules, and contractor calls',
  icon: 'wrench',
  version: '1.0.0',
  enabled_by_default: true,
  todo_types: [
    {
      type_tag: 'maintenance.service_call',
      label: 'Service Call',
      context_schema: {
        type: 'object',
        properties: {
          appliance_id: { type: 'string' },
          appliance_name: { type: 'string' },
          contractor_name: { type: 'string' },
          contractor_phone: { type: 'string' },
          service_type: { type: 'string' },
          estimated_cost: { type: 'number' },
        },
      },
    },
    {
      type_tag: 'maintenance.inspection',
      label: 'Inspection',
    },
    {
      type_tag: 'maintenance.task',
      label: 'Maintenance Task',
      context_schema: {
        type: 'object',
        properties: {
          appliance_id: { type: 'string' },
          appliance_name: { type: 'string' },
          service_type: { type: 'string' },
          location: { type: 'string' },
        },
      },
    },
  ],
  // Two web routes paired 1:1 with `routes` below (same pattern as Projects'
  // /projects + /projects/:id). The detail route is hidden from the sidebar
  // (empty label, negative order) — it's reached by clicking an appliance.
  web_routes: [
    {
      path: '/maintenance',
      sidebar_label: 'Maintenance',
      sidebar_order: 10,
    },
    {
      path: '/maintenance/equipment/:id',
      sidebar_label: '',
      sidebar_order: -1,
    },
  ],
  todoRenderers: {
    'maintenance.service_call': ServiceCallRenderer,
    'maintenance.inspection': ServiceCallRenderer, // Reuse for now
    'maintenance.task': MaintenanceTaskRenderer,
  },
  reducer: maintenanceReducer,
  saga: maintenanceSaga,
  routes: [LazyMaintenancePage, LazyEquipmentDetailPage],
};
